import React from 'react';
import {useAutoList} from '@netx/core-next/hooks/autoList/useAutoList';
import {
	getLabelValueAutoListOptionLabel,
	isLabelValueAutoListOptionSelected,
} from '@netx/core-next/utils/autoList';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {
	AutoListField,
	type AutoListCommonExtendProps,
} from '@netx/core-components/AutoListField';
import Tooltip from '@netx/core-components/Tooltip';
import {getCategoryAutoList} from '../../api';
import {type ICategoryAutoListItem} from '../../schema';

export type CategoryAutoListRef = React.ForwardedRef<HTMLDivElement>;

export interface CategoryAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends AutoListCommonExtendProps<
	ICategoryAutoListItem,
	Multiple,
	DisableClearable,
	false
> {
	permissionMatrixLevel?: number;
	omitTopLevel?: boolean;
	path?: string;
}

const CategoryAutoListInner = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		omitTopLevel,
		path,
		permissionMatrixLevel,
		filterOptions,
		getOptionLabel = getLabelValueAutoListOptionLabel,
		getOptionSelected = isLabelValueAutoListOptionSelected,
		onClose,
		onInputChange,
		onOpen,
		...props
	}: CategoryAutoListProps<Multiple, DisableClearable>,
	ref: CategoryAutoListRef,
) => {
	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			params: {permissionMatrixLevel},
			filterOptions: (data) => {
				data = data.filter((item) => item.value !== 1 || !omitTopLevel);
				return filterOptions ? filterOptions(data) : data;
			},
			queryFn: getCategoryAutoList,
		},
	);

	return (
		<Tooltip disabled={path == null} title={path ?? ''} placement="top-start">
			<AutoListField<ICategoryAutoListItem, Multiple, DisableClearable>
				ref={ref}
				label={i18n.t('i18n.category')}
				loadingOptions={loadingOptions}
				name="category-auto-list"
				placeholder={i18n.t('i18n.pleaseSelect')}
				isOptionEqualToValue={getOptionSelected}
				{...props}
				inputValue={queryStr}
				options={query.data ?? []}
				getOptionLabel={getOptionLabel}
				onClose={splitEventHandler(onClose, () => setIsOpen(false))}
				onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
				onInputChange={splitEventHandler(
					onInputChange,
					(event, value, reason) => setQueryStr(value),
				)}
			/>
		</Tooltip>
	);
};

export const CategoryAutoListPrimitive = React.forwardRef(
	CategoryAutoListInner,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: CategoryAutoListProps<Multiple, DisableClearable>,
	ref: CategoryAutoListRef,
) => JSX.Element;

export const CategoryAutoList = CategoryAutoListPrimitive;

export default CategoryAutoList;
